'use client';

import { useState } from 'react';
import { InputItems } from '~/types/entry-members-form';
import { MajorLicense } from '~/types/major-license';
import { Experience } from '~/types/experience';

interface Step1Props {
  majorLicenses: MajorLicense[];
  experiences: Experience[];
  inputItems: InputItems;
  licenseErrorMessage: string;
  experienceErrorMessage: string;
  isMobile: boolean;
  onNext: (input: InputItems) => void;
}

export default function Step1({
  majorLicenses,
  experiences,
  inputItems,
  licenseErrorMessage,
  experienceErrorMessage,
  isMobile,
  onNext,
}: Step1Props) {
  const [licenseCheck, setLicenseCheck] = useState<string[]>([]);
  const [experienceCheck, setExperienceCheck] = useState<string[]>([]);

  const handleLicenseChange = (value: string) => {
    setLicenseCheck((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleExperienceChange = (value: string) => {
    setExperienceCheck((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  return (
    <>
      <div className="p-horizontal-slide__form">
        <div className="p-horizontal-slide__header">
          <div className="u-flex -items-center u-gap8">
            <span className="c-label -bg-red">必須</span>
            <div className="u-font-bold">お持ちの資格</div>
            <span className="u-color-gray u-fs14">(複数選択可)</span>
          </div>
        </div>

        <div className="p-horizontal-slide__body">
          <div className="p-horizontal-slide__checkboxes">
            {majorLicenses.map((majorLicense) => {
              if (majorLicense.name === 'その他') {
                return (
                  <label
                    className="p-horizontal-slide__checkbox-label"
                    key="other"
                  >
                    <input
                      type="checkbox"
                      className="u-dis-none"
                      checked={licenseCheck.includes('その他')}
                      onChange={() => handleLicenseChange('その他')}
                    />
                    <span>その他</span>
                  </label>
                );
              } else if (
                [
                  '建築施工管理技士',
                  '土木施工管理技士',
                  '建築士',
                  '管工事施工管理技士',
                  '造園施工管理技士',
                ].includes(majorLicense.name)
              ) {
                return (
                  <label
                    className="p-horizontal-slide__checkbox-label"
                    key={majorLicense.name}
                  >
                    <input
                      type="checkbox"
                      className="u-dis-none"
                      checked={licenseCheck.includes(majorLicense.name)}
                      onChange={() => handleLicenseChange(majorLicense.name)}
                    />
                    <span>{majorLicense.name}</span>
                  </label>
                );
              } else {
                return majorLicense?.licenses.map((license) => (
                  <label
                    className="p-horizontal-slide__checkbox-label"
                    key={license.name}
                  >
                    <input
                      type="checkbox"
                      className="u-dis-none"
                      checked={licenseCheck.includes(license.name)}
                      onChange={() => handleLicenseChange(license.name)}
                    />
                    <span>
                      {license.name.replace(
                        /#電気主任技術者|#電気工事士|#電気工事施工管理技士/,
                        '',
                      )}
                    </span>
                  </label>
                ));
              }
            })}
          </div>
          <div className="p-entry-form__input__error -gc-1-4 u-h-1em u-mt8">
            {licenseErrorMessage}
          </div>
        </div>
      </div>

      <hr className={!isMobile ? 'u-mt20 u-mb40' : 'u-mt10 u-mb20'} />

      <div className="p-horizontal-slide__form">
        <div className="p-horizontal-slide__header">
          <div className="u-flex -items-center u-gap8">
            <span className="c-label -bg-red">必須</span>
            <div className="u-font-bold">ご経験</div>
            <span className="u-color-gray u-fs14">(複数選択可)</span>
          </div>
        </div>

        <div className="p-horizontal-slide__body">
          <div className="p-horizontal-slide__checkboxes">
            {experiences.map((experience) => (
              <label
                className="p-horizontal-slide__checkbox-label"
                key={experience.name}
              >
                <input
                  type="checkbox"
                  className="u-dis-none"
                  checked={experienceCheck.includes(experience.name)}
                  onChange={() => handleExperienceChange(experience.name)}
                />
                <span>
                  {experience.name != '該当なし'
                    ? `${experience.name}がある`
                    : experience.name}
                </span>
              </label>
            ))}
          </div>
          <div className="p-entry-form__input__error u-h-1em u-mt8">
            {experienceErrorMessage}
          </div>
        </div>
      </div>

      <div className="p-horizontal-slide__actions">
        <button
          type="button"
          className="c-button -horizontal-slide -arrow-right"
          onClick={() =>
            onNext({
              ...inputItems,
              license: licenseCheck,
              experience: experienceCheck,
            })
          }
        >
          次へ進む
        </button>
      </div>
    </>
  );
}
